/**
 * Semantic Color Tokens
 * Source: Design system camino.pdf (Pages 3-4)
 *
 * These tokens map semantic meanings to primitive colors,
 * with support for light and dark modes.
 */

import cdsColors from "./cdsColors";

// Primitive color references for convenience
const {
  cdsGrey, cdsIndigo, cdsOrange, cdsRed, cdsGreen,
} = cdsColors;

const white = "#FFFFFF";
const offWhite = "#FCFAF8"; // From existing colors.ts creamBg

type ColorToken = {
  light: string;
  dark: string;
};

type ColorCategory = {
  [key: string]: ColorToken;
};

/**
 * Helper function to add inverse variants to semantic colors
 * Inverse variants swap the light and dark mode values
 */
function addInverseVariants<T extends Record<string, ColorCategory>>(
  colors: T,
): T & Record<string, Record<string, ColorToken>> {
  const result = {} as any;

  for (const [category, tokens] of Object.entries(colors)) {
    result[category] = {};
    for (const [tokenName, values] of Object.entries(tokens)) {
      // Keep original token
      result[category][tokenName] = values;
      // Add inverse token (swap light/dark)
      result[category][`${tokenName}.inverse`] = {
        light: values.dark,
        dark: values.light,
      };
    }
  }

  return result;
}

/**
 * Base semantic color definitions with light and dark mode values
 */
const baseSemanticColors = {
  content: {
    primary: {
      light: cdsGrey[800], // #3A3A3A
      dark: white,
    },
    secondary: {
      light: cdsGrey[600], // #6C6C6C
      dark: cdsGrey[100], // #F2F2F2
    },
    tertiary: {
      light: cdsGrey[500], // #949494
      dark: cdsGrey[400], // #B4B4B4
    },
    disabled: {
      light: cdsGrey[200], // #E8E8E8
      dark: cdsGrey[700], // #585858
    },
    info: {
      light: cdsColors.brand[700], // #2F4BC9
      dark: cdsColors.brand[50], // #EFF6FF
    },
    infoBold: {
      light: cdsColors.brand[900], // #0A043C
      dark: cdsGrey[200], // #E8E8E8
    },
    notice: {
      light: cdsOrange[500], // #F59511
      dark: cdsOrange[500], // #F59511
    },
    noticeBold: {
      light: cdsOrange[800], // #E56C0A
      dark: cdsOrange[200], // #FACA81
    },
    negative: {
      light: cdsRed[500], // #EE522F
      dark: cdsRed[500], // #EE522F
    },
    negativeBold: {
      light: cdsRed[900], // #B22F17
      dark: cdsRed[200], // #ED9D97
    },
    positive: {
      light: cdsGreen[800], // #60A359
      dark: cdsGreen[800], // #60A359
    },
    positiveBold: {
      light: cdsGreen[900], // #3D6838
      dark: cdsGreen[300], // #A9E7A6
    },
  },

  background: {
    primary: {
      light: offWhite,
      dark: cdsGrey[900], // #1A1A1A
    },
    documentFieldRest: {
      light: "#F6F0EA", // offWhite (#FCFAF8) nudged 4% lightness toward black in HSL — darker well
      dark: "#222222",  // #1A1A1A page bg nudged lighter — correct well direction for dark mode
    },
    secondary: {
      light: white,
      dark: cdsGrey[800], // #3A3A3A
    },
    tertiary: {
      light: cdsGrey[100], // #F2F2F2
      dark: cdsGrey[700], // #585858
    },
    disabled: {
      light: cdsGrey[100], // #F2F2F2
      dark: cdsGrey[700], // #585858
    },
    info: {
      light: cdsIndigo[100], // #E0E7FF
      dark: cdsIndigo[300], // #A5B4FC
    },
    notice: {
      light: cdsOrange[100], // #FCDFB3
      dark: cdsOrange[900], // #DB5508
    },
    negative: {
      light: cdsRed[100], // #FFCFD0
      dark: cdsRed[900], // #B22F17
    },
    positive: {
      light: cdsGreen[100], // #DBF3DA
      dark: cdsGreen[900], // #3D6838
    },
  },

  // Status accents for persistent alarm-weight signals.
  // No `ok` — healthy state renders in default text colors. Green is reserved
  // for transient action feedback (toasts), never persistent UI state.
  status: {
    danger: {
      light: cdsRed[600], // #E0492F
      dark: cdsRed[400], // #EB5D4B
    },
    warning: {
      light: cdsOrange[600], // #F18B0F
      dark: cdsOrange[400], // #F6A62C
    },
  },

  border: {
    extraStrong: {
      light: cdsGrey[400], // #B4B4B4
      dark: cdsGrey[800], // #3A3A3A
    },
    strong: {
      light: cdsGrey[300], // #D8D8D8
      dark: cdsGrey[700], // #585858
    },
    default: {
      light: cdsGrey[200], // #E8E8E8
      dark: cdsGrey[700], // #585858
    },
    subtle: {
      light: cdsGrey[100], // #F2F2F2
      dark: cdsGrey[800], // #3A3A3A
    },
    notice: {
      light: cdsOrange[500], // #F59511
      dark: cdsOrange[500], // #F59511
    },
    negative: {
      light: cdsRed[500], // #EE522F
      dark: cdsRed[500], // #EE522F
    },
    positive: {
      light: cdsGreen[800], // #60A359
      dark: cdsGreen[800], // #60A359
    },
  },
} as const;

/**
 * Semantic colors with inverse variants automatically added
 * Each color token now has a corresponding inverse variant (e.g., primary -> primaryInverse)
 */
export const semanticColors = addInverseVariants(baseSemanticColors);

export type SemanticColors = typeof semanticColors;

/**
 * Helper to get the color value for the current color mode
 */
export const getSemanticColor = (
  category: keyof SemanticColors,
  token: string,
  mode: "light" | "dark" = "light",
): string => {
  const tokenObj = semanticColors[category][token];
  if (!tokenObj) {
    console.warn(`Semantic color not found: ${String(category)}.${token}`);
    return "#FF00FF"; // Magenta fallback for debugging
  }
  return tokenObj[mode];
};

/**
 * Helper function to generate Chakra UI semantic tokens from semantic colors
 */
function generateChakraTokens<T extends Record<string, ColorCategory>>(
  colors: T,
  categoryPrefix: Record<string, string>,
) {
  const result: Record<string, { default: string; _dark: string }> = {};

  for (const [category, tokens] of Object.entries(colors)) {
    const prefix = categoryPrefix[category] || category;
    for (const [tokenName, values] of Object.entries(tokens)) {
      const key = `${prefix}.${tokenName}`;
      result[key] = {
        default: values.light,
        _dark: values.dark,
      };
    }
  }

  return result;
}

/**
 * Chakra UI semantic tokens format
 * Use this with extendTheme({ semanticTokens: { colors: chakraSemanticTokens } })
 * Automatically includes inverse variants for all colors
 */
export const chakraSemanticTokens = generateChakraTokens(semanticColors, {
  content: "content",
  background: "bg",
  border: "border",
});

export default semanticColors;
